import React from 'react';
import { AlertCircle, Loader2, RotateCcw, Square } from 'lucide-react';
import { SkillRunState } from '@/types';
import { NodeSkillStage } from '../../hooks/useNodeSkillSession';

interface DetailPageSkillPanelProps {
  runState: SkillRunState;
  stage: NodeSkillStage | null;
  /** 取消（调 workflowCancel，状态回 idle） */
  onCancel: () => void;
  /** 错误重试（重新发起本技能执行） */
  onRetry?: () => void;
}

const STATUS_LABELS: Record<string, string> = {
  running: '执行中',
  waiting_ask: '待确认',
  error: '执行失败',
};

/** 品牌色（与 PipelineView / AskPopover 同源）：waiting_ask 状态圆点 */
const ACCENT = '#6D5BF5';

/**
 * 贴底执行条（§4.5.1）：内容区 flex-col 布局最后一个元素，mt-auto 压底通栏条（执行中不挤压中部空间）
 * - 仅状态行：图标 + 状态词 + 阶段详情（步骤/百分比并入详情后缀，不再独立占位）；
 * - waiting_ask：品牌色圆点 + 「请在下方弹窗完成确认」，交互已迁节点底部弹窗（DetailPageAskPopover）；
 * - error：红色 AlertCircle + 错误信息截断一行（hover 展开全部）+ 重试；
 * - idle/completed 不渲染（completed 产出由架构条目与分屏区承载）。
 */
export const DetailPageSkillPanel: React.FC<DetailPageSkillPanelProps> = ({
  runState,
  stage,
  onCancel,
  onRetry,
}) => {
  const { status, pendingAsk, error } = runState;

  // idle：未启动；completed：结果已落节点数据，执行条整体隐藏
  if (status === 'idle' || status === 'completed') return null;

  // 步骤/百分比并入阶段详情后缀（如「技能执行中 · page_design · 2/6」）
  const stepText = pendingAsk && (pendingAsk.totalSteps ?? 1) > 1
    ? `步骤 ${pendingAsk.currentStep ?? 1}/${pendingAsk.totalSteps}`
    : runState.progress != null
      ? `${Math.round(runState.progress * 100)}%`
      : null;

  const detail = status === 'waiting_ask'
    ? '请在下方弹窗完成确认'
    : status === 'error'
      ? error || ''
      : [
        stage ? `${stage.label}${stage.detail ? ` · ${stage.detail}` : ''}` : '',
        stepText,
      ].filter(Boolean).join(' · ');

  const isActive = status === 'running' || status === 'waiting_ask';

  return (
    <div
      className="mt-auto shrink-0 rounded-[20px] bg-white/[0.04] border border-white/[0.08] px-8 h-[88px] flex items-center gap-4"
      onDoubleClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {status === 'running' && <Loader2 size={26} className="animate-spin text-white/70 shrink-0" />}
      {status === 'waiting_ask' && (
        <span className="w-3 h-3 rounded-full shrink-0" style={{ background: ACCENT }} />
      )}
      {status === 'error' && <AlertCircle size={26} className="text-red-400/90 shrink-0" />}
      <span className="text-[22px] font-medium text-white/90 shrink-0">
        {STATUS_LABELS[status] ?? status}
      </span>
      {detail && (
        <span
          className="flex-1 min-w-0 text-[20px] text-white/45 truncate hover:whitespace-normal hover:text-white/65 transition-colors"
          title={status === 'error' ? error : undefined}
        >
          {detail}
        </span>
      )}
      {!detail && <span className="flex-1" />}
      {isActive && (
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-2 px-5 h-12 rounded-2xl bg-white/[0.06] border border-white/[0.12] text-[22px] text-white/70 hover:bg-white/[0.12] hover:text-white/90 transition-all shrink-0"
        >
          <Square size={20} />
          取消
        </button>
      )}
      {status === 'error' && onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="flex items-center gap-2 px-5 h-12 rounded-2xl bg-white/[0.06] border border-white/[0.12] text-[22px] text-white/70 hover:bg-white/[0.12] hover:text-white/90 transition-all shrink-0"
        >
          <RotateCcw size={20} />
          重试
        </button>
      )}
    </div>
  );
};
